import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ErrorActions } from 'src/app/error/store/error.slice';

import { SeoPropertiesDto } from '@app/api/marketplace/models';

import { getServerErrorForField } from '../../helpers/form-helper';
import { Language } from '../../interfaces/language';
import { LanguageCodesService } from '../../services/language-codes.service';
import { notBlank } from '../../validators/not-blank.validator';

@Component({
  selector: 'app-localized-seo-subform',
  templateUrl: './localized-seo-subform.component.html',
  styleUrls: ['./localized-seo-subform.component.scss'],
})
export class LocalizedSeoSubformComponent implements OnInit, OnChanges {
  static DEFAULT_LANGUAGE = 'hun';
  static VALIDATORS = [Validators.required, notBlank];
  separatorKeysCodes: number[] = [ENTER, COMMA, SPACE];

  @Input() formGroupAbstract!: AbstractControl;
  @Input() title?: string;
  @Input() readOnly = false;

  form!: UntypedFormGroup;
  metaTags: { [key: string]: string[] } = {};
  metaInputs!: UntypedFormGroup;

  languageSelect: UntypedFormControl = new UntypedFormControl(null);

  constructor(
    private languageCodesService: LanguageCodesService,
    private store: Store,
    private translateService: TranslateService
  ) {}

  static buildForm(seoProperties: { [key: string]: SeoPropertiesDto }) {
    return new UntypedFormGroup(
      Object.fromEntries(
        Object.entries(seoProperties)
          .filter(([language, _]) => language === this.DEFAULT_LANGUAGE)
          .map(([language, localizedValue]) => [
            language,
            new UntypedFormGroup({
              friendlyUrl: new UntypedFormControl(''),
              metaDescription: new UntypedFormControl(
                localizedValue.metaDescription
              ),
              metaTitle: new UntypedFormControl(localizedValue.metaTitle),
              metaTags: new UntypedFormControl(localizedValue.metaTags),
            }),
          ])
      )
    );
  }

  private static buildSubForm(): AbstractControl<any, any> {
    return new UntypedFormGroup({
      friendlyUrl: new UntypedFormControl(null),
      metaDescription: new UntypedFormControl(null),
      metaTitle: new UntypedFormControl(null),
      metaTags: new UntypedFormControl([]),
    });
  }

  ngOnInit(): void {
    this.metaInputs = new UntypedFormGroup({
      [LocalizedSeoSubformComponent.DEFAULT_LANGUAGE]: new UntypedFormControl(
        ''
      ),
    });
    if (this.form.controls) {
      Object.keys(this.form.controls).forEach((e) => {
        const subform = this.form.controls[e];
        if (!this.metaInputs.get(e)) {
          this.metaInputs.addControl(e, new UntypedFormControl(''));
        }
        if (subform.value.productTags) {
          this.metaTags[e] = subform.value.productTags;
        }
      });
    }
    if (!this.form.get(LocalizedSeoSubformComponent.DEFAULT_LANGUAGE)) {
      this.form.addControl(
        LocalizedSeoSubformComponent.DEFAULT_LANGUAGE,
        LocalizedSeoSubformComponent.buildSubForm()
      );
      this.metaInputs.addControl(
        LocalizedSeoSubformComponent.DEFAULT_LANGUAGE,
        new UntypedFormControl('')
      );
      if (this.readOnly) {
        this.form.disable();
        this.metaInputs.disable();
      }
    }
  }

  ngOnChanges(): void {
    if (!this.formGroupAbstract) {
      throw Error('Attribute "formGroupAbstract" is required');
    }
    this.form = this.formGroupAbstract as UntypedFormGroup;
    if (this.form.controls) {
      Object.keys(this.form.controls).forEach((e) => {
        const subform = this.form.controls[e];
        if (subform.value.metaTags) {
          this.metaTags[e] = subform.value.metaTags;
        }
      });
    }
  }

  onAdd(): void {
    const selectedLanguageCode: string | null =
      this.languageSelect?.value?.code;
    if (selectedLanguageCode) {
      if (this.form.contains(selectedLanguageCode)) {
        this.store.dispatch(
          ErrorActions.handleError({
            traceId: 0,
            message: this.translateService.instant(
              'SHARED.LOCALIZED_SEO_SUBFORM.ALREADY_ADDED'
            ),
            errorCode: '',
            globalErrors: {},
            fieldErrors: {},
          })
        );
      } else {
        this.form.addControl(
          selectedLanguageCode,
          LocalizedSeoSubformComponent.buildSubForm()
        );
        this.metaInputs.addControl(
          selectedLanguageCode,
          new UntypedFormControl('')
        );

        this.languageSelect.reset();
      }
    } else {
      this.languageSelect.markAsTouched();
      window.setTimeout(() => {
        // keeps control from staying red forever
        this.languageSelect.markAsUntouched();
      }, 3000);
    }
  }

  onDelete(selectedLanguageCode: string): void {
    this.form.removeControl(selectedLanguageCode);
    this.form.markAsDirty();
  }

  getKeys(): string[] {
    if (!this.form) return [];
    if (!this.form.controls) return [];
    return Object.keys(this.form.controls);
  }

  getNameByCode(code: string): string {
    return this.languageCodesService.getNameByCode(code);
  }

  getLanguages(): Language[] {
    return this.languageCodesService.availableLanguages;
  }

  nameFunction(item: Language): string {
    return item.name;
  }

  equalsFunction(a: Language, b: Language): boolean {
    return a && b && a.code === b.code;
  }

  matchFunction(item: Language, searchPattern: string): boolean {
    const fuzzyMatch = (_item: string, _searchPattern: string): boolean => {
      _searchPattern = _searchPattern.toLowerCase();
      _item = _item.toLowerCase();
      _searchPattern = '.*' + _searchPattern.split('').join('.*') + '.*';
      const re = new RegExp(_searchPattern);
      return re.test(_item);
    };

    return (
      fuzzyMatch(item.code, searchPattern) ||
      fuzzyMatch(item.name, searchPattern)
    );
  }

  getServerError(fieldName: string): any {
    return getServerErrorForField(this.form, fieldName);
  }

  addTag(event: MatChipInputEvent, lang: string): void {
    const value = (event.value || '').trim();

    if (value) {
      if (this.metaTags[lang]) {
        this.metaTags[lang].push(value);
      } else {
        this.metaTags[lang] = [value];
      }
    }

    event.chipInput.clear();
    this.metaInputs.controls[lang].setValue(null);

    this.form.patchValue({ [lang]: { metaTags: this.metaTags[lang] } });
    this.form.markAsTouched();
  }

  removeTag(tag: string, lang: string): void {
    const index = this.metaTags[lang].indexOf(tag);

    if (index >= 0) {
      this.metaTags[lang].splice(index, 1);
    }

    this.form.patchValue({ [lang]: { metaTags: this.metaTags[lang] } });
  }
}
